import { dehydrate, QueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { GetStaticPaths } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { RenderBlocks } from '@/components/Blocks/RenderBlocks';
import LatestNewsCarousel from '@/components/Core/LatestNewsCarousel/LatestNewsCarousel';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import useQuery, { makeQuery } from '@/hooks/use-query';
import { createNewsListQuery, mapToNewsList } from '@/queries/news/list';
import {
  createNewsPostPagePreviewQuery,
  createNewsPostPageQuery,
  mapToNewsPostPage,
  mapToNewsPostPreviewPage,
} from '@/queries/news/single';

const newsListQuery = createNewsListQuery();
const newsListQueryVars = {
  limit: 500,
};

const getRelatedQueryVars = (slug: string, categoryId?: string) => {
  const relatedPostsQueryVars = {
    limit: 5,
    exclude: [slug],
    ...(categoryId && { category: categoryId }),
  };
  return relatedPostsQueryVars;
};

interface PageParams {
  slug: string;
  id: string;
}

const PostPage = ({ slug, id }: PageParams) => {
  const { query } = useRouter();
  const { preview } = query || {};

  const postQuery = preview
    ? createNewsPostPagePreviewQuery()
    : createNewsPostPageQuery();

  const postQueryVars = preview ? { id } : { slug };

  const { data } = useQuery(
    postQuery,
    postQueryVars,
    preview ? mapToNewsPostPreviewPage : mapToNewsPostPage
  );
  const { category } = data || {};

  const relatedPostsQueryVars = getRelatedQueryVars(slug, category?.id);

  const { data: relatedPosts } = useQuery(
    newsListQuery,
    relatedPostsQueryVars,
    mapToNewsList,
    {
      enabled: !!category?.id,
    }
  );

  const formattedDate =
    data?.publishedDate &&
    format(new Date(data?.publishedDate), 'dd MMMM, yyyy');

  const relatedPostsCarouselOptions = {
    loop: (relatedPosts && relatedPosts.length >= 4) || false,
    slides: {
      perView: 1.5,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 640px)': {
        slides: {
          perView: 2.5,
          spacing: 24,
        },
      },
      '(min-width: 768px)': {
        slides: {
          perView: 3.5,
          spacing: 32,
        },
      },
      '(min-width: 1280px)': {
        slides: {
          perView: 4,
          spacing: 32,
        },
      },
    },
  };

  const meta = {
    title: data?.seo.title || data?.title,
    description: data?.seo.description || data?.sell,
    image: {
      url: data?.seo.image?.url || data?.thumbnail,
    },
  };

  return (
    <MainLayout withNavSpacer>
      <CustomMeta {...meta} />
      <div className="pb-page space-y-page">
        <article className="container-sm space-y-4 py-4 md:space-y-12">
          <div className="space-y-6 md:space-y-12">
            <h1 className="text-2xl font-black uppercase text-cream md:text-4xl">
              {data?.title}
            </h1>

            {data?.heroImage?.url && (
              <div className="relative space-y-2 pb-4">
                <Image
                  width={1000}
                  height={1000}
                  className="object-cover"
                  src={data?.heroImage.url}
                  alt="About"
                />
                {data?.heroImage.caption && (
                  <p className="text-sm italic text-cream">
                    {data?.heroImage.caption}
                  </p>
                )}
              </div>
            )}

            <div className="text-lg font-bold text-cream">
              {[data?.category?.name, formattedDate]
                .filter(Boolean)
                .join(' | ')}
            </div>
          </div>
          {data?.content && (
            <RenderBlocks
              className="space-y-4 md:space-y-8"
              blocks={data?.content}
            />
          )}
        </article>

        {relatedPosts && relatedPosts?.length > 0 && (
          <div className="container-md space-y-6">
            <div className="w-full">
              <h2 className="block font-dharma text-4xl font-black uppercase italic text-cream md:text-5xl">
                Related Posts
              </h2>
            </div>
            <LatestNewsCarousel
              items={relatedPosts}
              options={relatedPostsCarouselOptions}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PostPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const queryClient = new QueryClient();
  const data = await queryClient.fetchQuery(
    ['query', newsListQuery, newsListQueryVars],
    async () => {
      return makeQuery(newsListQuery, newsListQueryVars, mapToNewsList);
    }
  );

  const paths = data?.map((post) => ({
    params: { slug: post.slug },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ params }: { params: PageParams }) => {
  const { slug } = params;
  const postQuery = createNewsPostPageQuery();
  const queryClient = new QueryClient();
  const postQueryVars = { slug };

  const post = await queryClient.fetchQuery(
    ['query', postQuery, postQueryVars],
    async () => {
      return makeQuery(postQuery, postQueryVars, mapToNewsPostPage);
    }
  );
  console.log('🚀 ~ file: [slug].tsx:186 ~ getStaticProps ~ post:', post);

  if (!post?.id) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  const { category } = post || {};

  const relatedPostsQueryVars = getRelatedQueryVars(slug, category?.id);

  if (category?.id) {
    await queryClient.prefetchQuery(
      ['query', newsListQuery, relatedPostsQueryVars],
      async () => {
        return makeQuery(newsListQuery, relatedPostsQueryVars, mapToNewsList);
      }
    );
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      slug,
      id: post.id,
    },
    revalidate: 60,
  };
};
